import { resolveSafeViewerUrl } from "./safeViewerUrl.ts";
import { signedBoundUrl } from "./boundUrlSet.ts";
import { encodeBase64Url } from "../security/boundUrl.ts";

/**
 * A web browser cannot frame a site that refuses it, so Smart Manager on the
 * web hands that site to the desktop app's own browser instead of a new tab:
 * `smartmanager://open?url=<address>`, which electron/openLink.js parses
 * (and holds the page until the user has seen its address there, because any
 * page can fire the link). When the server bound the address for this user,
 * `&bound=<base64url JSON BoundUrl>` carries that binding, so the desktop
 * window (signed in as the same user) can verify it and offer its Load tap.
 *
 * A web page cannot ask whether an app handles a scheme. The launch either
 * takes focus from this page (the app, or the browser's "Open Smart
 * Manager?" prompt) within DESKTOP_HANDOFF_FALLBACK_MS, or nothing happened
 * and the app is most likely not installed. Losing focus is weak evidence:
 * the prompt's Cancel, Safari's "address is invalid" alert or switching apps
 * all do it too. So the answer remembered per browser only picks what to try
 * first, and every handoff keeps the viewer with its retry, download and new
 * tab (desktopHandoffState), so a wrong guess never dead-ends.
 */

export const DESKTOP_APP_SCHEME = "smartmanager";
export const DESKTOP_HANDOFF_FALLBACK_MS = 1_500;
/** Kept equal to MAX_OPEN_URL_LENGTH in electron/openLink.js. */
export const DESKTOP_OPEN_URL_MAX_LENGTH = 2_048;
const STATUS_KEY = "smartmanager.desktopApp";

/** What this browser learned from its last handoff. */
export type DesktopAppStatus = "unknown" | "installed" | "missing";

/** The link that opens `url` in the desktop app's own browser, or null for an address it would refuse. */
export function desktopOpenLink(url: unknown): string | null {
  if (typeof url !== "string") return null;
  const safeUrl = resolveSafeViewerUrl(url);
  if (!safeUrl || safeUrl.length > DESKTOP_OPEN_URL_MAX_LENGTH) return null;
  const link = `${DESKTOP_APP_SCHEME}://open?url=${encodeURIComponent(safeUrl)}`;
  const bound = signedBoundUrl(safeUrl);
  return bound
    ? `${link}&bound=${encodeBase64Url(new TextEncoder().encode(JSON.stringify(bound)))}`
    : link;
}

let cachedStatus: DesktopAppStatus | null = null;
const statusListeners = new Set<() => void>();

function readStoredStatus(): DesktopAppStatus {
  try {
    const stored = window.localStorage.getItem(STATUS_KEY);
    return stored === "installed" || stored === "missing" ? stored : "unknown";
  } catch {
    return "unknown";
  }
}

/** What the last handoff in this browser showed: the app opened, nothing opened, or never tried. */
export function desktopAppStatus(): DesktopAppStatus {
  if (typeof window === "undefined") return "unknown";
  cachedStatus ??= readStoredStatus();
  return cachedStatus;
}

function setDesktopAppStatus(status: Exclude<DesktopAppStatus, "unknown">): void {
  cachedStatus = status;
  try {
    window.localStorage.setItem(STATUS_KEY, status);
  } catch {
    // Private mode: the answer lasts for this page only.
  }
  for (const listener of statusListeners) listener();
}

/** Calls `listener` when desktopAppStatus or a desktopHandoffState changes; returns the unsubscribe (useSyncExternalStore shape). */
export function onDesktopAppStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}

/**
 * False while the page has no user action to launch with. Browsers refuse an
 * app launch without one, which would read as "not installed", so a spoken
 * or automatic request does not try.
 */
export function canLaunchDesktopApp(): boolean {
  if (typeof navigator === "undefined") return false;
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return activation ? activation.isActive : true;
}

export interface DesktopHandoffCallbacks {
  /** The app (or the browser's prompt for it) took focus. */
  onOpened?: () => void;
  /** Nothing took focus in time: most likely the app is not installed. */
  onMissing?: () => void;
}

/**
 * Where the handoff of one address stands in this page: never tried, waiting
 * for the app to take focus, sent (focus left the page; the app most likely
 * opened it), or nothing took focus.
 */
export type DesktopHandoffState = "idle" | "opening" | "sent" | "missing";

const handoffStates = new Map<string, Exclude<DesktopHandoffState, "idle">>();

function handoffKey(url: unknown): string | null {
  return typeof url === "string" ? resolveSafeViewerUrl(url) : null;
}

/** The latest handoff of `url` in this page (the viewer shows it and its fallbacks). */
export function desktopHandoffState(url: unknown): DesktopHandoffState {
  const key = handoffKey(url);
  return (key && handoffStates.get(key)) || "idle";
}

function setHandoffState(key: string, state: Exclude<DesktopHandoffState, "idle">): void {
  handoffStates.set(key, state);
  for (const listener of statusListeners) listener();
}

function clearHandoffState(key: string): void {
  if (!handoffStates.delete(key)) return;
  for (const listener of statusListeners) listener();
}

let cancelWatch: (() => void) | null = null;

function launch(link: string): void {
  // Firefox shows an error page in place of this one for a scheme it does
  // not know; from a hidden frame the failure stays out of sight.
  if (/\bfirefox\//i.test(navigator.userAgent)) {
    const frame = document.createElement("iframe");
    frame.hidden = true;
    // in-app-browser exception: custom-scheme launch of the Smart Manager desktop app, not a website.
    frame.src = link;
    document.body.appendChild(frame);
    window.setTimeout(() => frame.remove(), DESKTOP_HANDOFF_FALLBACK_MS * 2);
    return;
  }
  // in-app-browser exception: custom-scheme launch of the Smart Manager desktop app, not a website.
  window.location.assign(link);
}

/**
 * Opens `url` in the Smart Manager desktop app's own browser. Returns false,
 * launching nothing, for an address the app would refuse or when the page
 * has no user action to launch with (canLaunchDesktopApp); the caller then
 * shows the page's own explanation.
 */
export function handOffToDesktop(url: unknown, callbacks: DesktopHandoffCallbacks = {}): boolean {
  const link = desktopOpenLink(url);
  const resolvedKey = handoffKey(url);
  if (!link || !resolvedKey || typeof window === "undefined" || !canLaunchDesktopApp()) return false;
  const key: string = resolvedKey;
  // A newer handoff replaces one still waiting; that address goes back to never tried.
  cancelWatch?.();

  const onLeave = () => {
    if (document.visibilityState === "hidden" || !document.hasFocus()) settle(true);
  };
  const timer = window.setTimeout(() => settle(false), DESKTOP_HANDOFF_FALLBACK_MS);
  function stop() {
    window.clearTimeout(timer);
    window.removeEventListener("blur", onLeave);
    document.removeEventListener("visibilitychange", onLeave);
    if (cancelWatch === cancel) cancelWatch = null;
  }
  function cancel() {
    stop();
    clearHandoffState(key);
  }
  function settle(opened: boolean) {
    stop();
    setHandoffState(key, opened ? "sent" : "missing");
    setDesktopAppStatus(opened ? "installed" : "missing");
    if (opened) callbacks.onOpened?.();
    else callbacks.onMissing?.();
  }
  cancelWatch = cancel;
  setHandoffState(key, "opening");
  window.addEventListener("blur", onLeave);
  document.addEventListener("visibilitychange", onLeave);
  launch(link);
  return true;
}
